import { type FocusEvent, type KeyboardEvent, type MouseEvent } from 'react';

import { tokens } from '../../../../theme/generated/tokens';
import { getTreeMapLabelLines } from './_utils';
import { TreeMapItem } from './types';

// Figma: --data/treemap-hover-overlay — no dedicated token, matches Figma spec.
const HOVER_OVERLAY_COLOR = 'rgba(0,0,0,0.08)';

const CELL_RADIUS = 4;
const CELL_GAP_STROKE_WIDTH = 2;
const DASHED_STROKE_WIDTH = 1.5;
const DASHED_ARRAY = '4 3';
// Cells smaller than this (px) are too small to render any content legibly.
export const MIN_RENDERABLE_SIZE = 5;

// The DS `Spinner` (MUI CircularProgress) renders HTML, so it can't be
// embedded directly in this SVG cell tree without a <foreignObject>; it also
// doesn't match the Figma track+arc spec below. Custom SVG paths instead.
// Spinner path data extracted from the Figma SVG assets (20×20 viewBox).
// Arc (rotating piece): fill = grey-800 (#112934) per Figma --fill-0.
const SPINNER_ARC_PATH =
  'M10 0C15.5228 0 20 4.47715 20 10H18.1818C18.1818 5.48131 14.5187 1.81818 10 1.81818C5.48131 1.81818 1.81818 5.48131 1.81818 10C1.81818 14.5187 5.48131 18.1818 10 18.1818V20C4.47715 20 0 15.5228 0 10C0 4.47715 4.47715 0 10 0Z';
// Track (static ring): fill = surface-raised (#EFF2F5) per Figma --fill-0.
const SPINNER_TRACK_PATH =
  'M18.1818 10C18.1818 5.48131 14.5187 1.81818 10 1.81818C5.48131 1.81818 1.81818 5.48131 1.81818 10C1.81818 14.5187 5.48131 18.1818 10 18.1818V20C4.47715 20 0 15.5228 0 10C0 4.47715 4.47715 0 10 0C15.5228 0 20 4.47715 20 10C20 15.5228 15.5228 20 10 20V18.1818C14.5187 18.1818 18.1818 14.5187 18.1818 10Z';
const SPINNER_SIZE = 20;

// SVG <text> elements cannot resolve CSS custom properties for fontFamily.
// Hardcode the same value used by XAxisTick/YAxisTick throughout the DS chart components.
const FONT_BODY = 'Poppins, sans-serif';

// Cell content layout (px) — Figma spec measurements.
const TEXT_INSET_X = 8;
const NAME_BASELINE_Y = 16;
const LINE_HEIGHT = 12;
const FOOTER_BOTTOM_INSET = 10;
const SMALL_LABEL_FONT_SIZE = 10;
const LARGE_LABEL_FONT_SIZE = 11;
const SUBTITLE_OPACITY = 0.65;
const FOOTER_OPACITY = 0.72;

// Minimum cell dimensions (px) at which each content row is legible enough
// to show — Figma-derived breakpoints for the default cell renderer.
const SMALL_LABEL_WIDTH_THRESHOLD = 90;
const SMALL_LABEL_HEIGHT_THRESHOLD = 56;
const SUBTITLE_MIN_WIDTH = 64;
const SUBTITLE_MIN_HEIGHT = 42;
const FOOTER_MIN_WIDTH = 92;
const FOOTER_MIN_HEIGHT = 54;
const NAME_MIN_WIDTH = 34;
const NAME_MIN_HEIGHT = 18;

export type TreeMapCellProps = TreeMapItem & {
  x: number;
  y: number;
  width: number;
  height: number;
  isHovered: boolean;
  onCellClick?: (id: string, event?: MouseEvent<Element>) => void;
  onHoverChange: (id: string | null) => void;
  onMouseMove?: (id: string, clientX: number, clientY: number) => void;
};

export const TreeMapCell = ({
  id,
  name,
  color,
  textColor,
  subtitle,
  footer,
  footerColor,
  borderColor,
  borderDashed,
  loading = false,
  tooltip,
  x,
  y,
  width,
  height,
  isHovered,
  onCellClick,
  onHoverChange,
  onMouseMove,
}: TreeMapCellProps) => {
  // Skip degenerate cells produced by recharts layout (parent group nodes
  // or cells too small to display anything meaningful).
  if (width < MIN_RENDERABLE_SIZE || height < MIN_RENDERABLE_SIZE) {
    return null;
  }

  // Figma: loading cells use --fg/structural/secondary for text,
  // non-loading cells use the caller-supplied textColor or fall back to
  // --fg/structural/primary-on-inverse (white, for dark data-blue cells).
  const resolvedTextColor = loading
    ? tokens.theme.fg.structural.secondary
    : textColor ?? tokens.theme.fg.structural.primaryOnInverse;

  // Figma: solid-border gap uses white; dashed border uses the supplied
  // borderColor or falls back to --border/structural/neutral.
  const stroke = borderDashed
    ? borderColor ?? tokens.theme.border.structural.neutral
    : tokens.theme.border.structural.neutralSubtle;
  const strokeWidth = borderDashed
    ? DASHED_STROKE_WIDTH
    : CELL_GAP_STROKE_WIDTH;
  const strokeDasharray = borderDashed ? DASHED_ARRAY : undefined;

  // Figma: loading cells use --bg/structural/surface-raised.
  const bgColor = loading ? tokens.theme.bg.structural.surfaceRaised : color;

  const labelLines = getTreeMapLabelLines(name, width);
  const labelFontSize =
    width < SMALL_LABEL_WIDTH_THRESHOLD || height < SMALL_LABEL_HEIGHT_THRESHOLD
      ? SMALL_LABEL_FONT_SIZE
      : LARGE_LABEL_FONT_SIZE;
  const showSubtitle =
    !loading &&
    width > SUBTITLE_MIN_WIDTH &&
    height > SUBTITLE_MIN_HEIGHT &&
    !!subtitle;
  const showFooter =
    !loading &&
    width > FOOTER_MIN_WIDTH &&
    height > FOOTER_MIN_HEIGHT &&
    !!footer;
  const showName = width > NAME_MIN_WIDTH && height > NAME_MIN_HEIGHT;

  // Spinner: centered in the cell, scaled from the 20×20 Figma viewBox.
  const spinnerX = x + width / 2 - SPINNER_SIZE / 2;
  const spinnerY = y + height / 2 - SPINNER_SIZE / 2;

  // Figma spinner arc fill = grey-800 (dark ink on loading surface).
  const spinnerArcFill = tokens.primitives.colors.grey[800];
  // Figma spinner track fill = surface-raised (same as the cell bg, creating a cut-out look).
  const spinnerTrackFill = tokens.theme.bg.structural.surfaceRaised;

  const handleClick = (event: MouseEvent<Element>) => onCellClick?.(id, event);
  const handleKeyDown = (event: KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onCellClick?.(id);
    }
  };
  const handleMouseMove = (event: MouseEvent<SVGGElement>) => {
    onMouseMove?.(id, event.clientX, event.clientY);
  };

  const showTooltipAtCellCenter = (event: FocusEvent<SVGGElement>) => {
    onHoverChange(id);
    if (!tooltip || !onMouseMove) {
      return;
    }

    const rect = event.currentTarget.getBoundingClientRect();
    onMouseMove(id, rect.left + rect.width / 2, rect.top + rect.height / 2);
  };

  // Accessible label built from name + subtitle + footer.
  const ariaLabel = [name, subtitle, footer].filter(Boolean).join(' · ');

  return (
    <g
      role={onCellClick ? 'button' : undefined}
      tabIndex={onCellClick ? 0 : undefined}
      aria-label={ariaLabel}
      style={{
        cursor: onCellClick ? 'pointer' : 'default',
        outline: 'none',
        transition: 'opacity 140ms ease, filter 140ms ease',
      }}
      onMouseEnter={() => onHoverChange(id)}
      onMouseLeave={() => onHoverChange(null)}
      onMouseMove={tooltip ? handleMouseMove : undefined}
      onFocus={showTooltipAtCellCenter}
      onBlur={() => onHoverChange(null)}
      onKeyDown={handleKeyDown}
    >
      {/*
       * Only render the native SVG <title> when there is no rich tooltip.
       * When a rich tooltip is present, the <title> would show a native browser
       * tooltip on top of our custom one.
       */}
      {!tooltip && <title>{ariaLabel}</title>}

      {/* Cell background */}
      <rect
        x={x}
        y={y}
        width={width}
        height={height}
        fill={bgColor}
        stroke={stroke}
        strokeWidth={strokeWidth}
        strokeDasharray={strokeDasharray}
        rx={CELL_RADIUS}
        style={{ transition: 'fill 140ms ease' }}
        pointerEvents="none"
      />

      {/* Figma: --data/treemap-hover-overlay over the cell on hover */}
      {isHovered && (
        <rect
          x={x}
          y={y}
          width={width}
          height={height}
          fill={HOVER_OVERLAY_COLOR}
          rx={CELL_RADIUS}
          pointerEvents="none"
        />
      )}

      {/* Name — Figma P/12-M: 12px medium, body font */}
      {showName && (
        <text
          x={x + TEXT_INSET_X}
          y={y + NAME_BASELINE_Y}
          fill={resolvedTextColor}
          fontSize={labelFontSize}
          fontWeight={600}
          fontFamily={FONT_BODY}
          pointerEvents="none"
        >
          {labelLines.map((line, index) => (
            <tspan
              key={`${id}-line-${index}`}
              x={x + TEXT_INSET_X}
              dy={index === 0 ? 0 : LINE_HEIGHT}
            >
              {line}
            </tspan>
          ))}
        </text>
      )}

      {/* Subtitle — Figma P/10-R: 10px regular, body font */}
      {showSubtitle && (
        <text
          x={x + TEXT_INSET_X}
          y={y + NAME_BASELINE_Y + labelLines.length * LINE_HEIGHT}
          fill={resolvedTextColor}
          fontSize={SMALL_LABEL_FONT_SIZE}
          fontWeight={400}
          fontFamily={FONT_BODY}
          opacity={SUBTITLE_OPACITY}
          pointerEvents="none"
        >
          {subtitle}
        </text>
      )}

      {/* Footer — Figma P/10-R: 10px regular, body font, bottom-anchored */}
      {showFooter && (
        <text
          x={x + TEXT_INSET_X}
          y={y + height - FOOTER_BOTTOM_INSET}
          fill={footerColor ?? resolvedTextColor}
          fontSize={SMALL_LABEL_FONT_SIZE}
          fontWeight={400}
          fontFamily={FONT_BODY}
          opacity={FOOTER_OPACITY}
          pointerEvents="none"
        >
          {footer}
        </text>
      )}

      {/* Loading spinner — exact Figma SVG paths, centered in the cell */}
      {loading && (
        <g
          transform={`translate(${spinnerX}, ${spinnerY})`}
          pointerEvents="none"
        >
          {/* Track (static): surface-raised fill gives cut-out appearance */}
          <path d={SPINNER_TRACK_PATH} fill={spinnerTrackFill} />
          {/* Arc (rotating): dark ink, 3/4 circle */}
          <path d={SPINNER_ARC_PATH} fill={spinnerArcFill}>
            <animateTransform
              attributeName="transform"
              type="rotate"
              from="0 10 10"
              to="360 10 10"
              dur="0.8s"
              repeatCount="indefinite"
            />
          </path>
        </g>
      )}

      {/* Transparent click target on top */}
      <rect
        x={x}
        y={y}
        width={width}
        height={height}
        fill={tokens.primitives.colors.constants.transparent}
        rx={CELL_RADIUS}
        aria-hidden
        onClick={handleClick}
      />
    </g>
  );
};
